import { useCallback, useRef, useState } from 'react';

/**
 * Decides whether a list should render as a table or as cards based on whether the
 * table actually fits the available width — not on a phone breakpoint.
 *
 * @param {object}  params
 * @param {number}  params.minTableWidth - px width the table needs to render in full
 *                  (sum of column min-widths). Below this the list falls back to cards.
 * @param {('auto'|'table'|'cards')} [params.manualView='auto'] - user override; 'auto'
 *                  lets the measured width decide.
 * @returns {{ containerRef: function, view: 'table'|'cards', autoView: 'table'|'cards' }}
 *          Attach `containerRef` to a wrapper rendered in BOTH branches so the observer
 *          keeps measuring after a switch. It is a CALLBACK ref, so it also picks up
 *          containers that mount later (e.g. an AntD Modal body that only renders when open).
 */
const useResponsiveTableView = ({ minTableWidth, manualView = 'auto' }) => {
  const [containerWidth, setContainerWidth] = useState(null);
  const observerRef = useRef(null);

  // Only react to real width changes — guards against ResizeObserver feedback loops
  // (switching table<->cards changes height, which would otherwise re-fire the observer).
  const measure = useCallback((width) => {
    setContainerWidth((prev) => (prev === width ? prev : width));
  }, []);

  // Callback ref: React invokes it with the node when it mounts (including a modal
  // body that mounts only on open) and with null when it unmounts.
  const containerRef = useCallback((node) => {
    if (observerRef.current) {
      observerRef.current.disconnect();
      observerRef.current = null;
    }
    if (!node) return;

    // Synchronous read at attach time (before paint) avoids a cards→table flash.
    measure(node.clientWidth);

    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver((entries) => {
      const width = entries[0]?.contentRect?.width ?? node.clientWidth;
      measure(width);
    });
    observer.observe(node);
    observerRef.current = observer;
  }, [measure]);

  const autoView = containerWidth != null && containerWidth >= minTableWidth ? 'table' : 'cards';
  const view = manualView === 'auto' ? autoView : manualView;

  return { containerRef, view, autoView };
};

export default useResponsiveTableView;
